import React from 'react';

import { INVOICE_VIEW } from '../../../constants/abdmCopy';
import { formatDateIST } from '../../../utils/formatIST';
import { noteStyle } from '../noteStyle';

// Item-table columns after the name, in display order. A column is shown only when at least one
// row has a value for it (so a bill with no tax has no Tax column).
const COLUMNS = [
  { key: 'quantity', label: INVOICE_VIEW.colQty },
  { key: 'rate', label: INVOICE_VIEW.colRate, money: true },
  { key: 'discount', label: INVOICE_VIEW.colDiscount, money: true, deduction: true },
  { key: 'tax', label: INVOICE_VIEW.colTax, money: true },
  { key: 'surcharge', label: INVOICE_VIEW.colSurcharge, money: true },
];

const present = (v) => v !== null && v !== undefined;

// Values are shown exactly as received: no per-row amounts, no summing (the bill's own totals only).
const formatNumber = (v) => {
  const n = Number(v);
  return n.toLocaleString('en-IN', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 });
};
const formatMoney = (v, currency) => `${currency === 'INR' ? '₹' : `${currency} `}${formatNumber(v)}`;
// Quantity is shown as received (2.5, not 2.50): up to 3 decimals, no trailing zeros.
const formatQuantity = (v) => Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 });

function cellText(item, col, currency) {
  const v = item[col.key];
  if (!present(v)) return '—';
  if (!col.money) return formatQuantity(v);
  // Discount arrives as a positive number; show it as a deduction (a zero stays unsigned).
  return col.deduction && Number(v) !== 0 ? `− ${formatMoney(v, currency)}` : formatMoney(v, currency);
}

/**
 * InvoiceSection — the "Bill" section of ClinicalRecordRenderer: one card per invoice in
 * parsedSummary.invoices (the caller renders it only for a non-empty list).
 *
 * Props:
 *   invoices {object[]}
 *   isDark   {boolean}
 *   ui       {object} — the renderer's own colour/label tokens (textMain, textMuted, border,
 *                       cardBg, sectionLabel) so this section can't drift from its siblings
 */
export default function InvoiceSection({ invoices, isDark, ui }) {
  const { textMain, textMuted, border, cardBg, sectionLabel } = ui;
  const totalRow = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', fontSize: 13 };

  return (
    <div>
      <div style={sectionLabel}>{INVOICE_VIEW.section}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {invoices.map((inv, i) => {
          const currency = inv.currency || 'INR';
          const items = inv.items || [];
          const cols = COLUMNS.filter((c) => items.some((it) => present(it[c.key])));
          const heading = [`${INVOICE_VIEW.bill} ${inv.number || ''}`.trim(), formatDateIST(inv.date), inv.category].filter(Boolean);
          const showNet = present(inv.total_net);
          const showGross = present(inv.total_gross) && inv.total_gross !== inv.total_net;
          const cancelled = String(inv.status || '').toLowerCase() === 'cancelled';

          return (
            <div key={i} style={{ background: cardBg, border, borderRadius: 8, padding: 12 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 8px', fontSize: 12, color: textMuted }}>
                {heading.map((part, j) => (
                  <React.Fragment key={j}>
                    {j > 0 && <span aria-hidden="true">·</span>}
                    <span style={j === 0 ? { fontSize: 15, fontWeight: 700, color: textMain } : undefined}>{part}</span>
                  </React.Fragment>
                ))}
                {cancelled && (
                  <span style={{ ...noteStyle('err', isDark), marginTop: 0, padding: '1px 8px', fontSize: 11, fontWeight: 600, borderRadius: 99 }}>
                    {INVOICE_VIEW.cancelled}
                  </span>
                )}
              </div>

              {items.length > 0 && (
                // A phone-width screen scrolls the table inside the card rather than breaking the layout.
                <div style={{ overflowX: 'auto', marginTop: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: textMuted, textAlign: 'left', fontSize: 12 }}>
                        <th style={{ padding: '4px 8px 4px 0', fontWeight: 600, minWidth: 120 }}>{INVOICE_VIEW.colItem}</th>
                        {cols.map((c) => (
                          <th key={c.key} style={{ padding: '4px 0 4px 12px', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, k) => (
                        <tr key={k} style={{ borderTop: border, color: textMain }}>
                          <td style={{ padding: '6px 8px 6px 0', overflowWrap: 'anywhere' }}>{it.name || INVOICE_VIEW.unnamedItem}</td>
                          {cols.map((c) => (
                            <td key={c.key} style={{ padding: '6px 0 6px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{cellText(it, c, currency)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(showNet || showGross) && (
                <div style={{ borderTop: border, marginTop: 4, paddingTop: 6, color: textMain, fontWeight: 600 }}>
                  {showNet && (
                    <div style={totalRow}>
                      <span style={{ color: textMuted, fontWeight: 400 }}>{INVOICE_VIEW.total}</span>
                      <span>{formatMoney(inv.total_net, currency)}</span>
                    </div>
                  )}
                  {showGross && (
                    <div style={totalRow}>
                      <span style={{ color: textMuted, fontWeight: 400 }}>{INVOICE_VIEW.totalInclTax}</span>
                      <span>{formatMoney(inv.total_gross, currency)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
